import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { TableModule } from 'primeng/table';
import { CheckboxModule } from 'primeng/checkbox';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { MessageService } from 'primeng/api';
import { NextPage } from '@cvx/nextpage';
import { AdminService } from '../../../../services/admin.service';
import { CalService } from '../../../../services/base/cal.service';

interface APIUser {
  facilityId: string;
  displayName: string;
  emailAddress: string
  cai: string;
  ag: boolean;
  oa: boolean;
  ma: boolean;
  in: boolean;
  en: boolean;
  rsl: boolean;
  recID : number
}

interface User {
  userId: string;
  name: string;
  emailAddress: string;
  phone?: string;
  airGroup: boolean;
  operatingAssistant: boolean;
  maintainace: boolean;
  inspector: boolean;
  engineer: boolean;
  refineryShiftLeader: boolean;
}

@Component({
    selector: 'app-email-distribution',
    imports: [
        NextPage,
        CommonModule,
        FormsModule,
        TableModule,
        CheckboxModule,
        ButtonModule,
        InputTextModule,
        RouterModule,
    ],
    templateUrl: './email-distribution.component.html',
    styleUrl: './email-distribution.component.scss'
})
export class EmailDistributionComponent implements OnInit {
  // User search properties
  searchUserId: string = '';
  prevSearchUserId: string = '';
  filteredUsers: User[] = [];
  allUsers: User[] = [];
  isDataLoading : boolean = false; // Flag to show loading spinner
  noResultsFound: boolean = false;
  showUserDetails: boolean = false;
  deleteSucess: boolean = false;
selectedUser: User | null = null;
facilityID: string = '';
// Add new user properties
  newUserId: string = '';
  userVerified: boolean = false;
  userExists: boolean = false; // New property to track if user already exists
  newUserRoles: User = {
    userId: '',
    name: '',
    emailAddress: '',
    phone: '',
    airGroup: false,
    operatingAssistant: false,
    maintainace: false,
    inspector: false,
    engineer: false,
    refineryShiftLeader: false
  };

  constructor(
    private adminService: AdminService,
    private messageService: MessageService,
    private calService: CalService
  ) { }

  ngOnInit() {
    this.isDataLoading = true; // Set loading state to true
    this.facilityID = this.adminService.getCurrentFacilityId();
    this.getAdminEmailData();
  }
  getAdminEmailData() {
    this.adminService.getAdminEmailRoles().subscribe(response => {
      // Map API users to our User interface
      const apiUsers = Array.isArray(response) ? response as APIUser[] : [];
      this.allUsers = this.mapAPIUsersToUsers(apiUsers);
      this.filterUserData()
      this.isDataLoading = false; // Set loading state to false after data is loaded
    });
  }

  filterUserData(){
    let searchTerm = this.searchUserId.toLowerCase().trim();
    this.filteredUsers = this.allUsers.filter(user =>
      user.userId.toLowerCase().includes(searchTerm) ||
      user.name.toLowerCase().includes(searchTerm)
    );
  }

  // Map API user data to our User interface
  mapAPIUsersToUsers(apiUsers: APIUser[]): User[] {
    return apiUsers.map(apiUser => {
      return {
        userId: apiUser.cai, // cai is userId
        name: apiUser.displayName, // displayName is name
        emailAddress: apiUser.emailAddress, // emailAddress is emailAddress
        airGroup: apiUser.ag,
        operatingAssistant: apiUser.oa,
        maintainace: apiUser.ma,
        inspector: apiUser.in,
        engineer: apiUser.en,
        refineryShiftLeader: apiUser.rsl,
        recID : apiUser.recID
      };
    });
  }

  onSearchInputChange() {
    // If search field is empty, hide user details
    if (!this.searchUserId || this.searchUserId.trim() === '') {
      this.filteredUsers = [...this.allUsers];
      this.noResultsFound = false;
      this.showUserDetails = false;
    }
  }

  setFlag(){
    this.deleteSucess = false; 

  }

  searchUser() {
    this.deleteSucess = false; 
    this.isDataLoading = true; // Set loading state to true
    this.prevSearchUserId = this.searchUserId; 
    if (!this.searchUserId.trim()) {
      this.filteredUsers = [...this.allUsers];
      this.noResultsFound = false;
      this.showUserDetails = false;
      this.isDataLoading = false; // Set loading state to false
      return;
    }

    const searchTerm = this.searchUserId.toLowerCase();

    // Find users matching the search term (exact or partial)
    this.filteredUsers = this.allUsers.filter(user =>
      user.userId.toLowerCase().includes(searchTerm) ||
      user.name.toLowerCase().includes(searchTerm)
    );

    this.noResultsFound = this.filteredUsers.length === 0;
    // Always keep table view, don't show user details
    this.showUserDetails = false;
    this.isDataLoading = false; // Set loading state to false
  }

  saveChanges() {
    this.isDataLoading = true; // Set loading state to false

    if (this.selectedUser) {
      this.updateUser(this.selectedUser);
    }
  }

  updateUser(user: User) {
    this.deleteSucess = false; 
    // Create the request body according to API requirements
    const updateData = {
      cai: user.userId,
      facilityID: this.adminService.getCurrentFacilityId(),
      ag: user.airGroup,
      oa: user.operatingAssistant,
      ma: user.maintainace,
      in: user.inspector,
      en: user.engineer,
      rsl: user.refineryShiftLeader,
      emailAddress: user.emailAddress,
      displayName: user.name
    };
    // Call the service method to update the user roles
    this.adminService.updateEmail(updateData).subscribe(
      (response: any) => {
        if (response && response.success === false) {
          // Show error message with MessageService
          console.error('Failed to update user roles:', response.error);
          this.messageService.add({
            severity: 'error',
            summary: 'Update Failed',
            detail: `Failed to update user roles: ${response.error}`,
            life: 5000
          });
        } else {
          // Show success message with MessageService
          this.messageService.add({
            severity: 'success',
            summary: 'Update Successful',
            detail: `User ${user.userId} roles updated successfully!`,
            life: 3000
          });
        }
        this.isDataLoading = false; // Set loading state to false

      },
      (error) => {
        console.error('Error updating user roles:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'An error occurred while updating user roles. Please try again.',
          life: 5000
        });
        this.isDataLoading = false; // Set loading state to false

      }
    );
  }

  deleteUser(userId: string, email: string) {
    // Call the API to delete the user role
    this.isDataLoading = true; // Set loading state to false

    this.adminService.deleteEmailRole(userId, this.facilityID, email).subscribe(
      (response: any) => {
        if (response && response.success === false) {
          // Show error message with MessageService
          console.error('Failed to delete user:', response.error);
          this.messageService.add({
            severity: 'error',
            summary: 'Delete Failed',
            detail: `Failed to delete user: ${response.error}`,
            life: 5000
          });
        } else {
          this.searchUserId = ''; // Clear search input
          this.getAdminEmailData();
          this.deleteSucess = true; 
          // Show success message with MessageService
          this.messageService.add({
            severity: 'success',
            summary: 'User Deleted',
            detail: `User ${email} deleted successfully!`,
            life: 3000
          });
        }
        this.isDataLoading = false; // Set loading state to false

      },
      (error) => {
        console.error('Error deleting user:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'An error occurred while deleting the user. Please try again.',
          life: 5000
        });
        this.isDataLoading = false; // Set loading state to false

      }
    );
  }

  verifyUserId() {
    // Reset verification states
    this.isDataLoading = true; // Set loading state to false

    this.userVerified = false;
    this.userExists = false;

    if (!this.newUserId || this.newUserId.trim() === '') {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Please enter a User ID to verify',
        life: 3000
      });
      this.isDataLoading = false; // Set loading state to false

      return;
    }

    // First check if user already exists in our system
    const existingUser = this.allUsers.find(user =>
      user.userId.toLowerCase() === this.newUserId.toLowerCase()
    );

    if (existingUser) {
      // User already exists in the system
      this.userExists = true;
      this.userVerified = true;
      this.messageService.add({
        severity: 'warn',
        summary: 'User Exists',
        detail: `User with ID ${this.newUserId} already exists in the system`,
        life: 3000
      });
      this.isDataLoading = false; // Set loading state to false

      return;
    }

    // If user doesn't exist in our system, verify with directory service
    this.adminService.getUserInfoByUserID(this.newUserId).subscribe({
      next: (response: any) => {
        if (response && response.cai) {
          // User found in directory service
          this.userVerified = true;

          // Populate the user data
          this.newUserRoles = {
            userId: response.cai,
            name: response.displayName || '',
            emailAddress: response.emailAddress || '',
            phone: response.phoneNumber || '',
            airGroup: false,
            operatingAssistant: false,
            maintainace: false,
            inspector: false,
            engineer: false,
            refineryShiftLeader: false
          };

          this.messageService.add({
            severity: 'success',
            summary: 'User Verified',
            detail: 'User information retrieved successfully',
            life: 3000
          });
          this.isDataLoading = false; // Set loading state to false

        } else {
          // No user found
          this.userVerified = false;
          this.messageService.add({
            severity: 'error',
            summary: 'User Not Found',
            detail: `No information found for user ID ${this.newUserId}`,
            life: 3000
          });
          this.isDataLoading = false; // Set loading state to false

        }
      },
      error: (error) => {
        console.error('Error verifying user ID:', error);
        this.userVerified = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Verification Error',
          detail: 'An error occurred while verifying the user ID. Please try again.',
          life: 3000
        });
        this.isDataLoading = false; // Set loading state to false

      }
    });
  }

  clearUserId() {
    this.newUserId = '';
    this.userVerified = false;
    this.userExists = false;
    this.newUserRoles = {
      userId: '',
      name: '',
      emailAddress: '',
      phone: '',
      airGroup: false,
      operatingAssistant: false,
      maintainace: false,
      inspector: false,
      engineer: false,
      refineryShiftLeader: false
    };
  }

  addUser() {
    this.isDataLoading = true; // Set loading state to false

    if (!this.userVerified || this.userExists) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Please verify a valid user ID first',
        life: 3000
      });
      this.isDataLoading = false; // Set loading state to false

      return;
    }

    // Create the request body according to API requirements
    const userData = {
      cai: this.newUserRoles.userId,
      facilityID: this.adminService.getCurrentFacilityId(), // Changed from facilityId to facilityID
      ag: this.newUserRoles.airGroup,
      oa: this.newUserRoles.operatingAssistant,
      ma: this.newUserRoles.maintainace,
      in: this.newUserRoles.inspector,
      en: this.newUserRoles.engineer,
      rsl: this.newUserRoles.refineryShiftLeader,
      emailAddress: this.newUserRoles.emailAddress,
      displayName: this.newUserRoles.name
    };

    // Call the service method to add the new user
    this.adminService.addEmailRole(userData).subscribe({
      next: (response: any) => {
        if (response && response.success === false) {
          console.error('Failed to add user:', response.error);
          this.messageService.add({
            severity: 'error',
            summary: 'Add Failed',
            detail: `Failed to add user: ${response.error}`,
            life: 5000
          });
          this.isDataLoading = false; // Set loading state to false

        } else {
          // Add the new user to local arrays
          const newUser: User = {
            userId: this.newUserRoles.userId,
            name: this.newUserRoles.name,
            emailAddress: this.newUserRoles.emailAddress,
            phone: this.newUserRoles.phone,
            airGroup: this.newUserRoles.airGroup,
            operatingAssistant: this.newUserRoles.operatingAssistant,
            maintainace: this.newUserRoles.maintainace,
            inspector: this.newUserRoles.inspector,
            engineer: this.newUserRoles.engineer,
            refineryShiftLeader: this.newUserRoles.refineryShiftLeader
          };

          this.getAdminEmailData();

          // Clear the form
          this.clearUserId();

          // Show success message
          this.messageService.add({
            severity: 'success',
            summary: 'User Added',
            detail: `User ${userData.cai} added successfully!`,
            life: 3000
          });
        }
        this.isDataLoading = false; // Set loading state to false

      },
      error: (error) => {
        console.error('Error adding user:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'An error occurred while adding the user. Please try again.',
          life: 5000
        });
        this.isDataLoading = false; // Set loading state to false

      }

    });
  }
}
